import {
  associatePortalError,
  setPortalDiagnosticActor,
  portalToastError,
} from './portal-diagnostics';
import { portalSessionResponseSchema } from '@erp/shared';
import { z } from 'zod';
import { apiDownload, apiFetch, apiSend, ApiError, type DownloadResult } from '@/lib/api-client';

const CSRF_KEY = 'erp-portal-csrf';
let refreshPromise: Promise<boolean> | null = null;

export function storePortalCsrf(token: string): void {
  window.sessionStorage.setItem(CSRF_KEY, token);
}

export function clearPortalCsrf(): void {
  window.sessionStorage.removeItem(CSRF_KEY);
}

function currentPortalCsrf(): string | null {
  return window.sessionStorage.getItem(CSRF_KEY);
}

async function refreshPortalSession(): Promise<boolean> {
  refreshPromise ??= apiFetch('/api/v1/portal/auth/refresh', portalSessionResponseSchema, {
    method: 'POST',
    skipRefresh: true,
  })
    .then((response) => {
      storePortalCsrf(response.csrfToken);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

async function csrfToken(): Promise<string> {
  const stored = currentPortalCsrf();
  if (stored !== null) return stored;
  const response = await apiFetch(
    '/api/v1/portal/auth/csrf',
    z.object({ csrfToken: z.string().min(32) }),
    { method: 'POST', skipRefresh: true },
  );
  storePortalCsrf(response.csrfToken);
  return response.csrfToken;
}

interface PortalRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  authenticated?: boolean;
  csrf?: boolean;
}

export async function portalFetch<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: PortalRequestOptions = {},
): Promise<z.infer<TSchema>> {
  const authenticated = options.authenticated ?? true;
  if (!authenticated) setPortalDiagnosticActor(null);
  const attemptId = crypto.randomUUID();
  const context = {
    attemptId,
    action: `${options.method ?? 'GET'} ${path.split('?')[0] ?? '/api/v1/portal'}`,
    correlationId:
      z.object({ idempotencyKey: z.string().uuid() }).safeParse(options.body).data
        ?.idempotencyKey ?? null,
    targetId:
      path.match(/\/(?:documents|dunning|files|users|invitations)\/([a-zA-Z0-9_-]+)/u)?.[1] ?? null,
  };
  const execute = async () =>
    apiFetch(path, schema, {
      ...(options.method === undefined ? {} : { method: options.method }),
      body: options.body,
      skipRefresh: true,
      headers: {
        'x-portal-attempt': attemptId,
        ...(options.csrf === true ? { 'x-portal-csrf': await csrfToken() } : {}),
      },
    });
  const rememberIdentity = (result: z.infer<TSchema>) => {
    if (path === '/api/v1/portal/auth/me') {
      const user = z.object({ id: z.string() }).safeParse(result);
      if (user.success) setPortalDiagnosticActor(user.data.id);
    }
    return result;
  };
  try {
    return rememberIdentity(await execute());
  } catch (error) {
    if (
      !(error instanceof ApiError) ||
      error.status !== 401 ||
      !authenticated ||
      !(await refreshPortalSession())
    ) {
      associatePortalError(error, context);
      if (options.method !== undefined && options.method !== 'GET') portalToastError(error);
      throw error;
    }
    try {
      return rememberIdentity(await execute());
    } catch (retryError) {
      associatePortalError(retryError, context);
      portalToastError(retryError);
      throw retryError;
    }
  }
}

export async function portalSend(path: string, options: PortalRequestOptions): Promise<void> {
  if (options.authenticated === false) setPortalDiagnosticActor(null);
  const attemptId = crypto.randomUUID();
  const context = {
    attemptId,
    action: `${options.method ?? 'POST'} ${path.split('?')[0] ?? '/api/v1/portal'}`,
    correlationId: null,
    targetId:
      path.match(/\/(?:documents|dunning|files|users|invitations)\/([a-zA-Z0-9_-]+)/u)?.[1] ?? null,
  };
  const execute = async () =>
    apiSend(path, {
      method: options.method ?? 'POST',
      body: options.body,
      skipRefresh: true,
      headers: {
        'x-portal-attempt': attemptId,
        ...(options.csrf === true ? { 'x-portal-csrf': await csrfToken() } : {}),
      },
    });
  try {
    await execute();
  } catch (error) {
    if (
      !(error instanceof ApiError) ||
      error.status !== 401 ||
      options.authenticated === false ||
      !(await refreshPortalSession())
    ) {
      associatePortalError(error, context);
      if (options.method !== undefined && options.method !== 'GET') portalToastError(error);
      throw error;
    }
    try {
      await execute();
    } catch (retryError) {
      associatePortalError(retryError, context);
      portalToastError(retryError);
      throw retryError;
    }
  }
}

export async function portalDownload(
  path: string,
  fallbackFilename: string,
): Promise<DownloadResult> {
  const attemptId = crypto.randomUUID();
  const context = {
    attemptId,
    action: `GET ${path.split('?')[0] ?? '/api/v1/portal'}`,
    correlationId: null,
    targetId:
      path.match(/\/(?:documents|dunning|files|users|invitations)\/([a-zA-Z0-9_-]+)/u)?.[1] ?? null,
  };
  const execute = () =>
    apiDownload(path, fallbackFilename, {
      method: 'GET',
      skipRefresh: true,
      headers: { 'x-portal-attempt': attemptId },
    });
  try {
    return await execute();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401 || !(await refreshPortalSession())) {
      associatePortalError(error, context);
      throw error;
    }
    try {
      return await execute();
    } catch (retryError) {
      associatePortalError(retryError, context);
      portalToastError(retryError);
      throw retryError;
    }
  }
}
